import type { AuditEvent } from '../services/auditService';

const DEDUPE_WINDOW_MS = 5000;

function parseDetails(details: AuditEvent['details']): Record<string, unknown> {
  if (!details) return {};
  if (typeof details === 'string') {
    try {
      return JSON.parse(details) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return details as Record<string, unknown>;
}

function getAuditSource(ev: AuditEvent): string {
  const d = parseDetails(ev.details);
  return String(d.audit_source || '');
}

function normalizePath(details: Record<string, unknown>): string {
  const raw = String(details.path || details.url || '');
  try {
    if (raw.startsWith('http')) {
      return new URL(raw).pathname.split('?')[0].toLowerCase();
    }
  } catch {
    /* ignore */
  }
  return raw.split('?')[0].toLowerCase();
}

/** Stable key for “same action” within the dedupe time window. */
export function auditEventFingerprint(ev: AuditEvent): string {
  const d = parseDetails(ev.details);
  const method = String(d.method || '').toUpperCase();
  const path = normalizePath(d);
  return [
    ev.user_id,
    ev.event_type,
    ev.entity_type,
    ev.entity_id,
    method,
    path,
  ].join('|');
}

function mergeDetails(primary: AuditEvent, secondary: AuditEvent): Record<string, unknown> {
  const a = parseDetails(primary.details);
  const b = parseDetails(secondary.details);
  const sources = new Set<string>();
  for (const s of [a.audit_source, b.audit_source]) {
    if (s) sources.add(String(s));
  }
  return {
    ...b,
    ...a,
    audit_source: sources.size > 1 ? 'merged' : String(a.audit_source || b.audit_source || ''),
    audit_sources: Array.from(sources),
  };
}

function pickPreferred(existing: AuditEvent, incoming: AuditEvent): AuditEvent {
  const exSrc = getAuditSource(existing);
  const inSrc = getAuditSource(incoming);
  if (inSrc === 'api-middleware' && exSrc === 'client-interceptor') {
    return { ...incoming, details: mergeDetails(incoming, existing) };
  }
  if (exSrc === 'api-middleware') {
    return { ...existing, details: mergeDetails(existing, incoming) };
  }
  return { ...existing, details: mergeDetails(existing, incoming) };
}

/**
 * Collapse duplicate rows logged by both API middleware and the client interceptor
 * for the same user action within a short time window.
 */
export function dedupeAuditEvents(events: AuditEvent[], windowMs = DEDUPE_WINDOW_MS): AuditEvent[] {
  if (!events.length) return [];

  const sorted = [...events].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  const kept: AuditEvent[] = [];

  for (const ev of sorted) {
    const fp = auditEventFingerprint(ev);
    const ts = new Date(ev.timestamp).getTime();

    const dupIndex = kept.findIndex((k) => {
      if (auditEventFingerprint(k) !== fp) return false;
      return Math.abs(new Date(k.timestamp).getTime() - ts) <= windowMs;
    });

    if (dupIndex >= 0) {
      kept[dupIndex] = pickPreferred(kept[dupIndex], ev);
    } else {
      kept.push(ev);
    }
  }

  return kept;
}
